import React, { useState, useEffect } from "react";
import { FaPlus, FaArrowLeft, FaEdit, FaTrashAlt } from "react-icons/fa";
import { PetDrawer } from "../../../components/PetDrawer/PetDrawer";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import config from "../../../config";

const PetSection = () => {
  const navigate = useNavigate();
  const [pets, setPets] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingPet, setEditingPet] = useState(null);

  const [petTypes, setPetTypes] = useState([]);
  const [petBreeds, setPetBreeds] = useState([]);
  const [petSizes, setPetSizes] = useState([]);

  const token = localStorage.getItem("token");
  const decoded = token ? jwtDecode(token) : null;
  const client_id = decoded?.data?.customer_data?.id;

  // ✅ Fetch pet-related lists
  useEffect(() => {
    fetchPets();
    fetchPetTypes();
    fetchBreeds();
    fetchSizes();
  }, [client_id]);

  const fetchPetTypes = async () => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/customer/customerpettype`);
      if (res.data?.status) setPetTypes(res.data.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch pet types");
    }
  };

  const fetchBreeds = async () => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/customer/customerbreed`);
      if (res.data?.status) setPetBreeds(res.data.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch pet breeds");
    }
  };

  const fetchSizes = async () => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/customer/customerpetsize`);
      if (res.data?.status) setPetSizes(res.data.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch pet sizes");
    }
  };

  // ✅ Fetch all pets
  const fetchPets = async () => {
    if (!client_id) return;

    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/customer/getallpet`,
        { client_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.pets) {
        setPets(response.data.pets);
      } else {
        setPets([]);
      }
    } catch (error) {
      console.error("Error fetching pets:", error.response?.data || error);
      toast.error("Failed to fetch pets");
    }
  };

  // 🌟 Add or Edit
  const handleAddPet = () => {
    setEditingPet(null);
    setDrawerOpen(true);
  };

  const handleEditPet = (pet) => {
    setEditingPet(pet);
    setDrawerOpen(true);
  };

  // 🌟 Delete
  const handleDeletePet = async (petId) => {
    if (window.confirm("Are you sure you want to delete this pet?")) {
      try {
        const res = await axios.delete(
          `${config.API_BASE_URL}/customer/deletepet/${petId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.status === 200) {
          toast.success("Pet deleted successfully!");
          await fetchPets();
        }
      } catch (error) {
        console.error("Error deleting pet:", error.response?.data || error);
        toast.error("Failed to delete pet");
      }
    }
  };

  const handleSavePet = async () => {
    setDrawerOpen(false);
    setEditingPet(null);
    await fetchPets();
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingPet(null);
  };

  // ✅ Utility functions for mapping
  const getPetTypeName = (id) => petTypes.find((t) => t._id === id)?.name || "-";
  const getBreedName = (id) => petBreeds.find((b) => b._id === id)?.breed_name || "-";
  const getSizeLabel = (id) => petSizes.find((s) => s._id === id)?.size_label || "-";

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 bg-blue-900 text-white rounded hover:bg-blue-800 transition-shadow shadow"
          >
            <FaArrowLeft />
          </button>
          <h1 className="text-3xl font-extrabold text-orange-600">Pet List</h1>
        </div>
        <button
          className="flex items-center gap-2 px-5 py-2 rounded-lg bg-blue-900 text-white hover:bg-blue-800 shadow transition"
          onClick={handleAddPet}
        >
          <FaPlus /> Add Pet
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white shadow-lg rounded-xl border border-gray-200">
        <table className="w-full min-w-max divide-y divide-gray-200">
          <thead className="bg-orange-100">
            <tr>
              {["Pet Name", "Type", "Breed", "Age", "Size", "Vaccinated", "Actions"].map(
                (header) => (
                  <th
                    key={header}
                    className={`px-5 py-3 text-left text-blue-900 font-bold text-sm uppercase tracking-wider ${
                      header === "Actions" ? "text-center" : ""
                    }`}
                  >
                    {header}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pets.length > 0 ? (
              pets.map((pet, idx) => (
                <tr key={idx} className="hover:bg-orange-50 transition">
                  <td className="px-5 py-3 text-blue-900 font-semibold">
                    {pet?.name || "-"}
                  </td>
                  <td className="px-5 py-3 text-blue-900">
                    {getPetTypeName(pet?.pet_type)}
                  </td>
                  <td className="px-5 py-3 text-blue-900">
                    {getBreedName(pet?.breed)}
                  </td>
                  <td className="px-5 py-3 text-blue-900">{pet?.age || "-"}</td>
                  <td className="px-5 py-3 text-blue-900">
                    {getSizeLabel(pet?.size)}
                  </td>
                  <td className="px-5 py-3 text-blue-900">
                    {pet?.vaccinated === "yes" ? "Yes ✅" : "No ❌"}
                  </td>

                  {/* Actions */}
                  <td className="px-5 py-3 text-center">
                    <div className="flex justify-center items-center gap-3">
                      <button
                        title="Edit Pet"
                        onClick={() => handleEditPet(pet)}
                        className="text-blue-600 hover:text-blue-800 transition p-1 rounded-full hover:bg-blue-100"
                      >
                        <FaEdit />
                      </button>
                      <button
                        title="Delete Pet"
                        onClick={() => handleDeletePet(pet._id)}
                        className="text-red-600 hover:text-red-800 transition p-1 rounded-full hover:bg-red-100"
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  className="px-5 py-6 text-center text-gray-500 italic"
                  colSpan={7}
                >
                  No pets added yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Drawer */}
      <PetDrawer
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        onSave={handleSavePet}
        petToEdit={editingPet}
      />
    </div>
  );
};

export default PetSection;
